// /finance's Clients (finance redesign slice 3; on jobs, finance consolidation S9): which client earns what. Active (a
// client with an active job) by default, with No active job and All as a status TabGroup on the header's right with
// their counts, and a search by name in the table's own toolbar (finance-real round 7). Each row: what its recurring
// jobs for this period bill, their margin (price less claims, all of it where nobody claims yet, before any write-off,
// so never the books' profit) with its percent, what its sent invoices still ask, and its latest invoice sent with its
// day. Every figure is the server's (TD-102), and the footer totals the tab's billing clients, the agency's own
// client's costs apart (TD-105). A row opens the client in a drawer on this page (DD-051), whose strip reads the row's
// figures. Its jobs, quotes and invoices start on the client's own page, a step from there (DD-039), so this tab
// creates nothing. Below sm the money columns but the margin hide (DD-055).
import { TablePageBody } from "@/components/PageLayout/TablePageBody";
import { DataTable, type DataColumn } from "@/components/ui/DataTable";
import { FilterBar } from "@/components/ui/FilterBar";
import { MoneyText } from "@/components/ui/MoneyText";
import { TabGroup, type TabGroupConfig } from "@/components/ui/TabGroup";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { TabPending } from "../components/TabPending";
import { TotalsFooter, type TotalItem } from "../components/TotalsFooter";
import { useFinanceParams } from "../hooks/useFinanceParams";
import type { JobClientRow, JobClientsShow, JobClientTotals } from "../types";
import { clientName, invoiceDateLine } from "../utils/names";
import { matchesSearch } from "../utils/search";
import { useClientsShowTabs } from "./hooks/useClientsShowTabs";
import { useClientsTab } from "./hooks/useClientsTab";

const CLIENT = ENTITY_CONFIG.client;
const clientWord = CLIENT.label.toLowerCase();
const clientsWord = CLIENT.plural.toLowerCase();
// The user's job, a line item, rides main's `contract` key (the entityConfig shadow).
const jobWord = ENTITY_CONFIG.contract.label.toLowerCase();
const invoiceWord = ENTITY_CONFIG.invoice.label.toLowerCase();
const NONE = <span className="text-gray-400">·</span>;

const SHOWS: { value: JobClientsShow; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "no_active_job", label: `No active ${jobWord}` },
  { value: "all", label: "All" },
];

export const toShow = (value: string | null): JobClientsShow => (value === "no_active_job" || value === "all" ? value : "active");

/** A margin and its percent. */
function MarginCell({ row }: { row: JobClientRow }) {
  if (row.ownClient || row.marginPct === null) return NONE;
  return (
    <span className="whitespace-nowrap">
      <MoneyText amountMinor={row.marginMonthlyMinor} balance />
      <span className="text-xs text-gray-400">{` · ${row.marginPct}%`}</span>
    </span>
  );
}

const columns: DataColumn<JobClientRow>[] = [
  {
    key: "name", label: CLIENT.label, truncates: true,
    render: (r) => <span className="block truncate font-medium text-gray-900">{clientName(r.name, r.ownClient)}</span>,
  },
  {
    // The own client is never invoiced: its figure is what its work costs (TD-105), and says so.
    key: "billed", label: "Billed each month", align: "right", width: "hidden sm:table-cell w-40",
    render: (r) =>
      r.monthlyMinor === 0 ? (
        NONE
      ) : r.ownClient ? (
        <span className="flex flex-col items-end">
          <MoneyText amountMinor={r.monthlyMinor} />
          <span className="text-xs text-gray-500">cost</span>
        </span>
      ) : (
        <MoneyText amountMinor={r.monthlyMinor} />
      ),
  },
  { key: "margin", label: "Margin each month", align: "right", width: "w-36 sm:w-44", render: (r) => <MarginCell row={r} /> },
  { key: "due", label: "Still due", align: "right", width: "hidden sm:table-cell w-32", render: (r) => (r.ownClient ? NONE : <MoneyText amountMinor={r.dueMinor} />) },
  {
    key: "last", label: `Last ${invoiceWord}`, align: "right", width: "hidden sm:table-cell w-40",
    render: (r) =>
      r.lastInvoice ? (
        <span className="flex flex-col items-end">
          <MoneyText amountMinor={r.lastInvoice.invoiceMinor} />
          {/* Its one date line (invoiceDateLine): "Sent 18 Sep 2026", "Paid 15 Oct 2026". */}
          <span className="text-xs text-gray-400">{invoiceDateLine({ kind: "client", ...r.lastInvoice })}</span>
        </span>
      ) : (
        NONE
      ),
  },
];

/** The footer (finance-real round 7, R10): over the whole tab, whatever the search, which narrows the rows only. Each label
 * names its column's figure, and the own client's costs stay apart. */
function totalsOf(t: JobClientTotals): TotalItem[] {
  return [
    { label: "Billed each month", amountMinor: t.monthlyMinor, note: `the ${clientsWord} invoiced` },
    { label: "Margin each month", amountMinor: t.marginMonthlyMinor, note: "price less claims, before write-offs" },
    { label: "Still due", amountMinor: t.dueMinor },
    ...(t.ownCostsMinor !== 0 ? [{ label: `Own ${clientWord}'s costs each month`, amountMinor: t.ownCostsMinor }] : []),
  ];
}

/** The header's status tabs (finance-real round 7, H2): the page's tabs' config (DD-012), each count a badge, every tab
 * shown whatever its count. */
export function ClientsShowTabs({ config }: { config: TabGroupConfig }) {
  const { params, set } = useFinanceParams();
  const show = toShow(params.get("show"));
  const counts = useClientsShowTabs(show);
  return (
    <TabGroup
      items={SHOWS.map((s) => ({ id: s.value, label: s.label, badge: counts?.[s.value] }))}
      activeId={show}
      onTabChange={(id) => set({ show: id === "active" ? null : id }, true)}
      config={config}
    />
  );
}

export function ClientsTab({ onOpen }: { onOpen: (clientId: string) => void }) {
  const { params, set } = useFinanceParams();
  const show = toShow(params.get("show"));
  const q = params.get("q") ?? "";
  const query = useClientsTab(show);
  const filters = (
    <FilterBar
      selects={[]}
      search={{ value: q, onChange: (v) => set({ q: v || null }, true), placeholder: `Search ${clientsWord}` }}
      trailing={<span className="text-xs text-gray-500">{`Click a ${clientWord} to open it.`}</span>}
    />
  );
  // The filters are the table's own toolbar (finance-real round 7), so they stay put while it loads or fails, in its empty row.
  const rows = query.data ? query.data.clients.filter((r) => matchesSearch(q, [r.name])) : [];
  const empty = !query.data ? (
    <TabPending failed={query.isError} message={`Could not load the ${clientsWord}.`} retrying={query.isFetching} onRetry={() => void query.refetch()} />
  ) : show === "no_active_job" && query.data.clients.length === 0 ? (
    `Every ${clientWord} has an active ${jobWord}.`
  ) : (
    `No ${clientWord} matches.`
  );
  return (
    <TablePageBody>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={(r) => onOpen(r.id)}
        empty={empty}
        toolbar={filters}
        footer={query.data && <TotalsFooter items={totalsOf(query.data.totals)} />}
      />
    </TablePageBody>
  );
}
